import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  KeyRound,
} from 'lucide-react';
import { Button, Badge, Table, Modal, Input } from '@/components/ui';
import { userAdminService } from '@/services/userAdminService';
import type { AdminUser, CreateUserRequest, UpdateUserRequest } from '@/types/user';
import styles from './UsersPage.module.css';

const PAGE_SIZE = 20;

const ROLE_LABELS: Record<string, string> = {
  Admin: 'Admin',
  Doctor: 'Médico',
  Receptionist: 'Recepcionista',
  Patient: 'Paciente',
};

const ROLE_BADGE: Record<string, 'info' | 'success' | 'warning' | 'neutral'> = {
  Admin: 'info',
  Doctor: 'success',
  Receptionist: 'warning',
  Patient: 'neutral',
};

/**
 * Admin page for managing users.
 *
 * Features:
 * - Paginated table listing
 * - Create new user with roles
 * - Edit user profile and roles
 * - Activate / Deactivate users
 * - Reset password
 */
export function UsersPage() {
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [resetPwUser, setResetPwUser] = useState<AdminUser | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formCpf, setFormCpf] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formRoles, setFormRoles] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  // Reset password form
  const [newPassword, setNewPassword] = useState('');

  // Queries
  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', currentPage],
    queryFn: () => userAdminService.getUsers(currentPage, PAGE_SIZE),
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => userAdminService.getRoles(),
  });

  const users = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => userAdminService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      handleCloseCreate();
    },
    onError: () => setFormError('Erro ao criar usuário. Verifique os dados.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      userAdminService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      handleCloseEdit();
    },
    onError: () => setFormError('Erro ao atualizar usuário.'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (user: AdminUser) =>
      user.isActive
        ? userAdminService.deactivateUser(user.id)
        : userAdminService.activateUser(user.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      userAdminService.resetPassword(id, { newPassword: password }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      handleCloseResetPw();
    },
    onError: () => setFormError('Erro ao resetar senha.'),
  });

  // Handlers
  const handleOpenCreate = useCallback(() => {
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setFormCpf('');
    setFormPhone('');
    setFormRoles([]);
    setFormError(null);
    setIsCreateOpen(true);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setIsCreateOpen(false);
    setFormError(null);
  }, []);

  const handleOpenEdit = useCallback((user: AdminUser) => {
    setFormName(user.fullName);
    setFormEmail(user.email);
    setFormCpf(user.cpf ?? '');
    setFormPhone(user.phone ?? '');
    setFormRoles([...user.roles]);
    setFormError(null);
    setEditingUser(user);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditingUser(null);
    setFormError(null);
  }, []);

  const handleOpenResetPw = useCallback((user: AdminUser) => {
    setNewPassword('');
    setFormError(null);
    setResetPwUser(user);
  }, []);

  const handleCloseResetPw = useCallback(() => {
    setResetPwUser(null);
    setFormError(null);
  }, []);

  const handleRoleToggle = useCallback((roleName: string) => {
    setFormRoles((prev) =>
      prev.includes(roleName)
        ? prev.filter((r) => r !== roleName)
        : [...prev, roleName],
    );
  }, []);

  const handleCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setFormError(null);

      if (!formName.trim()) {
        setFormError('Nome é obrigatório.');
        return;
      }
      if (!formEmail.trim()) {
        setFormError('E-mail é obrigatório.');
        return;
      }
      if (formPassword.length < 8) {
        setFormError('Senha deve ter no mínimo 8 caracteres.');
        return;
      }
      if (formRoles.length === 0) {
        setFormError('Selecione pelo menos um papel.');
        return;
      }

      createMutation.mutate({
        fullName: formName.trim(),
        email: formEmail.trim(),
        password: formPassword,
        roles: formRoles,
        cpf: formCpf.replace(/\D/g, '') || undefined,
        phone: formPhone.replace(/\D/g, '') || undefined,
      });
    },
    [formName, formEmail, formPassword, formRoles, formCpf, formPhone, createMutation],
  );

  const handleUpdate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!editingUser) return;
      setFormError(null);

      if (!formName.trim()) {
        setFormError('Nome é obrigatório.');
        return;
      }
      if (formRoles.length === 0) {
        setFormError('Selecione pelo menos um papel.');
        return;
      }

      updateMutation.mutate({
        id: editingUser.id,
        data: {
          fullName: formName.trim(),
          roles: formRoles,
          cpf: formCpf.replace(/\D/g, '') || undefined,
          phone: formPhone.replace(/\D/g, '') || undefined,
        },
      });
    },
    [editingUser, formName, formRoles, formCpf, formPhone, updateMutation],
  );

  const handleResetPassword = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!resetPwUser) return;
      setFormError(null);

      if (newPassword.length < 8) {
        setFormError('Senha deve ter no mínimo 8 caracteres.');
        return;
      }

      resetPasswordMutation.mutate({
        id: resetPwUser.id,
        password: newPassword,
      });
    },
    [resetPwUser, newPassword, resetPasswordMutation],
  );

  const columns = [
    {
      key: 'user',
      header: 'Usuário',
      render: (user: AdminUser) => (
        <div className={styles.userInfo}>
          <span className={styles.userName}>{user.fullName}</span>
          <span className={styles.userEmail}>{user.email}</span>
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Papéis',
      render: (user: AdminUser) => (
        <div className={styles.roleList}>
          {user.roles.map((role) => (
            <Badge key={role} variant={ROLE_BADGE[role] ?? 'neutral'}>
              {ROLE_LABELS[role] ?? role}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: AdminUser) => (
        <Badge variant={user.isActive ? 'success' : 'error'}>
          {user.isActive ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (user: AdminUser) => (
        <div className={styles.actions}>
          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(user)}>
            Editar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleActiveMutation.mutate(user)}
            isLoading={toggleActiveMutation.isPending}
          >
            {user.isActive ? 'Desativar' : 'Ativar'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOpenResetPw(user)}
            leftIcon={<KeyRound size={14} />}
          >
            Senha
          </Button>
        </div>
      ),
    },
  ];

  const rolesCheckboxes = (
    <div className={styles.checkboxGroup}>
      <span className={styles.checkboxLabel}>Papéis</span>
      {roles.map((role) => (
        <label key={role.id} className={styles.checkboxItem}>
          <input
            type="checkbox"
            checked={formRoles.includes(role.name)}
            onChange={() => handleRoleToggle(role.name)}
          />
          {ROLE_LABELS[role.name] ?? role.name}
        </label>
      ))}
    </div>
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Usuários</h1>
          <p className={styles.subtitle}>
            Gerencie os usuários do sistema.
          </p>
        </div>
        <Button leftIcon={<Plus size={18} />} onClick={handleOpenCreate}>
          Novo Usuário
        </Button>
      </header>

      {isLoading ? (
        <div className={styles.emptyState} role="status" aria-busy="true">
          <p>Carregando usuários...</p>
        </div>
      ) : users.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <p>Nenhum usuário encontrado.</p>
        </div>
      ) : (
        <>
          <Table columns={columns} data={users} keyExtractor={(u) => u.id} />

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                leftIcon={<ChevronLeft size={16} />}
              >
                Anterior
              </Button>
              <span className={styles.paginationInfo}>
                Página {currentPage} de {totalPages} ({totalCount} usuários)
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                leftIcon={<ChevronRight size={16} />}
              >
                Próxima
              </Button>
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      <Modal isOpen={isCreateOpen} onClose={handleCloseCreate} title="Novo Usuário" size="md">
        <form onSubmit={handleCreate} className={styles.form} noValidate>
          <Input
            label="Nome completo"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Nome do usuário"
            required
            fullWidth
          />
          <Input
            label="Email"
            type="email"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            placeholder="email@exemplo.com"
            required
            fullWidth
          />
          <Input
            label="Senha"
            type="password"
            value={formPassword}
            onChange={(e) => setFormPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            required
            fullWidth
          />
          <div className={styles.formRow}>
            <Input
              label="CPF (opcional)"
              value={formCpf}
              onChange={(e) => setFormCpf(e.target.value)}
              placeholder="000.000.000-00"
              inputMode="numeric"
              fullWidth
            />
            <Input
              label="Telefone (opcional)"
              value={formPhone}
              onChange={(e) => setFormPhone(e.target.value)}
              placeholder="(00) 00000-0000"
              inputMode="tel"
              fullWidth
            />
          </div>
          {rolesCheckboxes}
          {formError && (
            <p className={styles.formError} role="alert">{formError}</p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={handleCloseCreate}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Criar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editingUser !== null}
        onClose={handleCloseEdit}
        title="Editar Usuário"
        size="md"
      >
        <form onSubmit={handleUpdate} className={styles.form} noValidate>
          <Input
            label="Nome completo"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Nome do usuário"
            required
            fullWidth
          />
          <Input
            label="Email"
            type="email"
            value={formEmail}
            disabled
            fullWidth
          />
          <div className={styles.formRow}>
            <Input
              label="CPF (opcional)"
              value={formCpf}
              onChange={(e) => setFormCpf(e.target.value)}
              placeholder="000.000.000-00"
              inputMode="numeric"
              fullWidth
            />
            <Input
              label="Telefone (opcional)"
              value={formPhone}
              onChange={(e) => setFormPhone(e.target.value)}
              placeholder="(00) 00000-0000"
              inputMode="tel"
              fullWidth
            />
          </div>
          {rolesCheckboxes}
          {formError && (
            <p className={styles.formError} role="alert">{formError}</p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={handleCloseEdit}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={resetPwUser !== null}
        onClose={handleCloseResetPw}
        title="Resetar Senha"
        size="sm"
      >
        <form onSubmit={handleResetPassword} className={styles.form} noValidate>
          <p>
            Redefinir senha para <strong>{resetPwUser?.fullName}</strong> ({resetPwUser?.email}).
          </p>
          <Input
            label="Nova senha"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            required
            fullWidth
          />
          {formError && (
            <p className={styles.formError} role="alert">{formError}</p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={handleCloseResetPw}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={resetPasswordMutation.isPending}>
              Resetar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
